import { Button } from "@platform/design-system";
import { ReactNode } from "react";

import { isValidRFPStatus } from "../../../../constants/rfp";

interface FinalDecisionConfig {
  message: ReactNode;
  showReview: boolean;
  buttonConfig: { show: boolean; text: string };
}

interface GetFinalDecisionConfigParams {
  rfpStatus: string | null;
  hasFormDraft: boolean;
  hasFinalDecisions: boolean;
  onFinalDecisionClick: VoidFunction;
}

export function getFinalDecisionConfig({
  rfpStatus,
  hasFormDraft,
  hasFinalDecisions,
  onFinalDecisionClick,
}: GetFinalDecisionConfigParams): FinalDecisionConfig {
  // Edit existing decision coverages view
  if (hasFinalDecisions) {
    return {
      message: "",
      showReview: true,
      buttonConfig: { show: true, text: "Edit Final Decisions" },
    };
  }

  // In-progress final decisions draft view
  if (hasFormDraft) {
    return {
      message: (
        <>
          A final draft has been started and not completed yet.
          <Button
            sx={{ fontSize: "inherit", textTransform: "none" }}
            variant="tertiary"
            onClick={onFinalDecisionClick}
          >
            Continue working on final decisions
          </Button>
        </>
      ),
      showReview: false,
      buttonConfig: { show: true, text: "Continue final decisions" },
    };
  }

  // Final decisions ready to be created view
  if (isDeliveredStatus(rfpStatus)) {
    return {
      message: "No decisions have been made.",
      showReview: false,
      buttonConfig: { show: true, text: "Make Final Decisions" },
    };
  }

  // Final decisions not ready to start
  return {
    message:
      "Once this RFP has been sent to at least one carrier, you will be able to return to this step and begin inputting data related to final decisions.",
    showReview: false,
    buttonConfig: { show: false, text: "" },
  };
}

function isDeliveredStatus(status: string | null): boolean {
  if (!isValidRFPStatus(status)) {
    return false;
  }
  // The following statuses indicate the RFP has been delivered to a carrier
  return [
    "outToMarket",
    "deliveredPartial",
    "deliveredFinal",
    "complete",
    "withdrawn",
  ].includes(status);
}
